import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type OrderItemStepTodo from '@this/src/domain/arrangement/order_item_step_todo';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@this/src/components/shared/ui/feedbacks/modal';
import { ConfirmModal } from '@this/src/components/shared/ui/feedbacks/modal/confirm_modal';
import { Alert } from '@this/src/components/shared/ui/feedbacks/alert';
import { useAITTheme } from '@this/shared/ui/theme';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Datepicker } from '@this/shared/ui/inputs/datepicker';
import type { OrderItemStepTodoFieldStoreResponseArgs } from '@this/src/domain/arrangement/order_item_step_todo_field_store';
import {
  OrderItemStepTodoFieldStore,
  convertOrderItemStepTodoFieldStoreResponseArgs
} from '@this/src/domain/arrangement/order_item_step_todo_field_store';
import { appendScript, Fetcher } from '@this/src/util';
import Notification from '@this/src/notification';
import { OrderItemStatus } from '@this/src/domain/order_item/order_item_status';
import { OrderItemJobType } from '@this/src/domain/order_item/order_item_job_type';
import OrderItemStepWorkDialogContent from './order_item_step_work_dialog_content';
import type { OrderItemStepWorkDialogContentHandle } from './order_item_step_work_dialog_content';
import OrderItemStepDurationCounter from './order_item_step_duration_counter';
import RestartSequencePopover from './restart_sequence_popover';
import type { JobType } from './restart_sequence_popover';

type PusherConfigResponse = {
  pusherKey: string;
  pusherChannelPrefix: string;
};

interface PusherChannel {
  name: string;
  bind: <T = unknown>(event: string, callback: (data: T) => void) => void;
  unbind: (event?: string) => void;
}

interface PusherInstance {
  subscribe: (channel: string) => PusherChannel;
  unsubscribe: (channel: string) => void;
  disconnect: () => void;
}

interface Props {
  serviceId: number;
  todo: OrderItemStepTodo | null;
  onClose: (isFetch?: boolean) => void;
}

type CriticalError = { type: 'error' | 'message'; message: string } | { type: 'trip_id'; tripId: number };

const JOB_TYPE_LABELS: Record<JobType, string> = {
  reserve: '新規',
  change: '変更',
  cancel: 'キャンセル'
};

const getDefaultSnoozeTo = (): Date => {
  const target = new Date();
  target.setDate(target.getDate() + 1);
  target.setHours(9, 0, 0, 0);
  return target;
};

type TripIdDisplayProps = {
  tripId: number;
  variant?: 'header' | 'footer';
};

const TripIdWithArrangementLink: React.FC<TripIdDisplayProps> = ({ tripId, variant = 'footer' }) => {
  const { theme } = useAITTheme();
  const isHeader = variant === 'header';
  const fontSize = '0.875rem';

  return (
    <span style={{ fontSize, color: isHeader ? '#fff' : undefined }}>
      <span style={{ color: isHeader ? undefined : theme.grayTextColor, marginRight: '4px' }}>旅程番号:</span>
      <span>{tripId}</span>
      <span style={{ marginLeft: '4px' }}>
        （
        <Link
          href={`/arrangement/virtual_counter?trip_id=${tripId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize, ...(isHeader ? { color: '#fff' } : {}) }}
        >
          手配画面
        </Link>
        ）
      </span>
    </span>
  );
};

const OrderItemStepWorkDialog: React.FC<Props> = ({ serviceId, todo, onClose }) => {
  const { theme } = useAITTheme();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [store, setStore] = useState<OrderItemStepTodoFieldStore | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [criticalErrors, setCriticalErrors] = useState<CriticalError[]>([]);
  const [restartAnchorEl, setRestartAnchorEl] = useState<HTMLElement | null>(null);
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);
  const [restartParams, setRestartParams] = useState<{ jobType: JobType; targetStatus: number } | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [newUserMessageReceived, setNewUserMessageReceived] = useState(false);
  const [cancelRequestReceived, setCancelRequestReceived] = useState<{
    isOrderItemCancel: boolean;
    orderItemId?: number;
  } | null>(null);
  const [showSnoozeForm, setShowSnoozeForm] = useState(false);
  const [snoozeTo, setSnoozeTo] = useState<Date | undefined>(undefined);
  const [snoozing, setSnoozing] = useState(false);
  const stateTodo = store?.todo;
  const displayTripId = stateTodo?.tripId ?? todo?.tripId;
  const traceId = useMemo(() => todo?.traceId, [todo]);

  const pusherRef = useRef<PusherInstance | null>(null);
  const tripChannelRef = useRef<PusherChannel | null>(null);
  const pusherKeyRef = useRef<string | null>(null);
  const pusherChannelPrefixRef = useRef<string | null>(null);
  const timeoutIdsRef = useRef<number[]>([]);
  const [pusherReady, setPusherReady] = useState(false);
  const contentRef = useRef<OrderItemStepWorkDialogContentHandle>(null);

  const fetchOrderItem = useCallback(async () => {
    setLoading(true);
    setStore(null);
    if (!traceId) {
      setLoading(false);
      return;
    }

    Fetcher.get<OrderItemStepTodoFieldStoreResponseArgs>(`/arrangement/order_item/${traceId}`)
      .then(response => {
        setLoading(false);
        setStore(
          new OrderItemStepTodoFieldStore(convertOrderItemStepTodoFieldStoreResponseArgs(response), traceId)
        );
      })
      .catch(error => {
        setLoading(false);
        const errorMessage = error.response?.data?.error || '商品情報が取得できませんでした';
        console.error('Failed to fetch order item:', error);
        setErrors([errorMessage]);
      });
  }, [traceId]);

  const handleNewMessage = useCallback(
    (data: { type?: string; order_item_id?: number }) => {
      if (data.type === 'user') {
        setNewUserMessageReceived(true);
        contentRef.current?.refetchAndScrollToBottom();
      } else if (data.type === 'cancel_request') {
        const currentOrderItemId = store?.orderItem?.id;
        const isOrderItemCancel = data.order_item_id !== undefined;
        if (isOrderItemCancel && currentOrderItemId !== data.order_item_id) {
          return;
        }
        setCancelRequestReceived({
          isOrderItemCancel,
          orderItemId: data.order_item_id
        });
        contentRef.current?.refetchAndScrollToBottom();
      }
    },
    [store?.orderItem?.id]
  );

  const initPusher = useCallback(() => {
    const currentKey = pusherKeyRef.current;
    const currentPrefix = pusherChannelPrefixRef.current;
    if (!currentKey || currentPrefix === null) {
      const timeoutId = window.setTimeout(initPusher, 100);
      timeoutIdsRef.current.push(timeoutId);
      return;
    }
    if (typeof Pusher !== 'undefined') {
      pusherRef.current = new Pusher(currentKey, { encrypted: true });
      setPusherReady(true);
    } else {
      const timeoutId = window.setTimeout(initPusher, 100);
      timeoutIdsRef.current.push(timeoutId);
    }
  }, []);

  const subscribeTrip = useCallback(
    (tripId: number) => {
      const channelPrefix = pusherChannelPrefixRef.current;
      if (channelPrefix === null || !pusherRef.current) {
        const timeoutId = window.setTimeout(() => subscribeTrip(tripId), 100);
        timeoutIdsRef.current.push(timeoutId);
        return;
      }
      if (tripChannelRef.current) {
        pusherRef.current.unsubscribe(tripChannelRef.current.name);
      }
      tripChannelRef.current = pusherRef.current.subscribe(channelPrefix + tripId.toString());
      tripChannelRef.current.bind('new_message', handleNewMessage);
    },
    [handleNewMessage]
  );

  const unsubscribeTrip = useCallback(() => {
    if (tripChannelRef.current && pusherRef.current) {
      tripChannelRef.current.unbind('new_message');
      pusherRef.current.unsubscribe(tripChannelRef.current.name);
      tripChannelRef.current = null;
    }
  }, []);

  const fetchPusherConfig = useCallback(() => {
    Fetcher.get<PusherConfigResponse>('/arrangement/virtual_counter.json')
      .then(result => {
        pusherKeyRef.current = result.pusherKey;
        pusherChannelPrefixRef.current = result.pusherChannelPrefix;
        initPusher();
      })
      .catch(error => {
        console.error('Failed to fetch pusher config:', error);
      });
  }, [initPusher]);

  useEffect(() => {
    if (todo) {
      appendScript('https://js.pusher.com/3.0/pusher.min.js');
      fetchPusherConfig();
      setNewUserMessageReceived(false);
      setPusherReady(false);
    }

    return () => {
      timeoutIdsRef.current.forEach(window.clearTimeout);
      timeoutIdsRef.current = [];
      unsubscribeTrip();
    };
  }, [todo, fetchPusherConfig, unsubscribeTrip]);

  useEffect(() => {
    const tripId = store?.trip?.id;
    if (tripId && pusherReady && pusherRef.current) {
      subscribeTrip(tripId);
    }
  }, [store?.trip?.id, pusherReady, subscribeTrip]);

  const handleNewMessageModalClose = useCallback(() => {
    setNewUserMessageReceived(false);
    contentRef.current?.scrollToBottom();
  }, []);

  const fetchPause = useCallback(() => {
    if (!stateTodo) return;
    setUpdating(true);
    Fetcher.post('/arrangement/order_item_step_todos/todo_action.json', {
      order_item_step_id: stateTodo.id,
      action_type: 'pause'
    })
      .then(() => {
        onClose(true);
      })
      .catch(error => {
        const message = error.response?.data.message;
        if (!message) {
          const errorList = error.response?.data.errors ?? [];
          setErrors(typeof errorList === 'string' ? [errorList] : errorList);
          setCriticalErrors([]);
          onClose();
          return;
        }

        const newCriticalErrors: CriticalError[] = [];
        (Array.isArray(message) ? message : [message]).forEach(msg => {
          newCriticalErrors.push({ type: 'error', message: msg });
        });
        const description = error.response?.data.description;

        (Array.isArray(description) ? description : []).forEach(msg => {
          newCriticalErrors.push({ type: 'message', message: msg });
        });
        if (error.response?.data.trip_id) {
          newCriticalErrors.push({ type: 'trip_id', tripId: error.response?.data.trip_id });
        }
        setCriticalErrors(newCriticalErrors);
        onClose();
      })
      .finally(() => {
        setUpdating(false);
      });
  }, [stateTodo, setUpdating, setErrors, setCriticalErrors, onClose]);

  const fetchComplete = useCallback(() => {
    if (!store || !stateTodo) return;
    setUpdating(true);
    const params = {
      trip_id: store.trip?.id,
      ...store.order?.updateParams
    };
    Fetcher.post(`/arrangement/order_item_step_todos/${stateTodo.id}/finish.json`, params)
      .then(() => {
        onClose(true);
      })
      .catch(error => {
        const message = error.response?.data.message;
        if (!message) {
          const errorList = error.response?.data.errors ?? [];
          setErrors(typeof errorList === 'string' ? [errorList] : errorList);
          setCriticalErrors([]);
          return;
        }

        const newCriticalErrors: CriticalError[] = [];
        (Array.isArray(message) ? message : [message]).forEach(msg => {
          newCriticalErrors.push({ type: 'error', message: msg });
        });
        const description = error.response?.data.description;

        (Array.isArray(description) ? description : []).forEach(msg => {
          newCriticalErrors.push({ type: 'message', message: msg });
        });
        if (error.response?.data.trip_id) {
          newCriticalErrors.push({ type: 'trip_id', tripId: error.response?.data.trip_id });
        }
        setCriticalErrors(newCriticalErrors);
      })
      .finally(() => {
        setUpdating(false);
      });
  }, [store, stateTodo, setUpdating, setErrors, setCriticalErrors, onClose]);

  const fetchSave = useCallback(() => {
    if (!store || !stateTodo) return;
    setSaving(true);
    const params = {
      trip_id: store.trip?.id,
      ...store.order?.updateParams
    };
    Fetcher.post(`/arrangement/order_item_step_todos/${stateTodo.id}/save.json`, params)
      .then(() => {
        Notification.success('保存しました');
        fetchOrderItem();
      })
      .catch(error => {
        const message = error.response?.data.message;
        if (!message) {
          const errorList = error.response?.data.errors ?? [];
          setErrors(typeof errorList === 'string' ? [errorList] : errorList);
          setCriticalErrors([]);
          return;
        }

        const newCriticalErrors: CriticalError[] = [];
        (Array.isArray(message) ? message : [message]).forEach(msg => {
          newCriticalErrors.push({ type: 'error', message: msg });
        });
        const description = error.response?.data.description;

        (Array.isArray(description) ? description : []).forEach(msg => {
          newCriticalErrors.push({ type: 'message', message: msg });
        });
        if (error.response?.data.trip_id) {
          newCriticalErrors.push({ type: 'trip_id', tripId: error.response?.data.trip_id });
        }
        setCriticalErrors(newCriticalErrors);
      })
      .finally(() => {
        setSaving(false);
      });
  }, [store, stateTodo, fetchOrderItem]);

  const handleClose = useCallback(() => {
    if (stateTodo?.stepStatus === 'started') {
      fetchPause();
    } else {
      onClose();
    }
  }, [stateTodo, fetchPause, onClose]);

  const handleSecondaryClose = useCallback(() => {
    setErrors([]);
    setCriticalErrors([]);
  }, []);

  const handleRestartClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setRestartAnchorEl(event.currentTarget);
  }, []);

  const handleRestartPopoverClose = useCallback(() => {
    setRestartAnchorEl(null);
  }, []);

  const handleRestartConfirm = useCallback((jobType: JobType, targetStatus: number) => {
    setRestartAnchorEl(null);
    setRestartParams({ jobType, targetStatus });
    setRestartConfirmOpen(true);
  }, []);

  const handleRestartConfirmClose = useCallback((_e: object) => {
    setRestartConfirmOpen(false);
    setRestartParams(null);
  }, []);

  const handleRestartExecute = useCallback(
    (_e: object) => {
      if (!traceId || !restartParams) return;

      setRestarting(true);
      Fetcher.post('/arrangement/order_item_step_todos/restart_sequence.json', {
        trace_id: traceId,
        job_type: restartParams.jobType,
        target_status: restartParams.targetStatus
      })
        .then(() => {
          setRestartConfirmOpen(false);
          setRestartParams(null);
          onClose(true);
        })
        .catch(error => {
          const errorMessage = error.response?.data?.errors || 'シーケンスの再発火に失敗しました';
          setErrors(Array.isArray(errorMessage) ? errorMessage : [errorMessage]);
          setRestartConfirmOpen(false);
          setRestartParams(null);
        })
        .finally(() => {
          setRestarting(false);
        });
    },
    [traceId, restartParams, onClose]
  );

  const handleSubmit = useCallback(() => {
    setErrors([]);
    setCriticalErrors([]);
    if (store) {
      fetchComplete();
    } else {
      setErrors(['TODOが選択されていません']);
    }
  }, [store, fetchComplete]);

  const handleSave = useCallback(() => {
    setErrors([]);
    setCriticalErrors([]);
    if (store) {
      fetchSave();
    } else {
      setErrors(['TODOが選択されていません']);
    }
  }, [store, fetchSave]);

  const handleSnoozeClick = useCallback(() => {
    setSnoozeTo(getDefaultSnoozeTo());
    setShowSnoozeForm(true);
  }, []);

  const handleSnoozeCancel = useCallback(() => {
    setShowSnoozeForm(false);
    setSnoozeTo(undefined);
  }, []);

  const handleSnoozeSave = useCallback(() => {
    if (!store?.orderItem?.id || !snoozeTo) return;

    setSnoozing(true);
    const formatDate = (date: Date) => {
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}`;
    };
    const params = {
      order_item_id: store.orderItem.id,
      snooze_to: formatDate(snoozeTo)
    };
    Fetcher.post('/arrangement/order_item_snoozed_todos', params)
      .then(() => {
        setShowSnoozeForm(false);
        setSnoozeTo(undefined);
        if (stateTodo?.stepStatus === 'started') {
          fetchPause();
        } else {
          onClose(true);
        }
      })
      .catch(() => {
        setErrors(['スヌーズの保存に失敗しました']);
      })
      .finally(() => {
        setSnoozing(false);
      });
  }, [store, snoozeTo, stateTodo, fetchPause, onClose]);

  useEffect(() => {
    fetchOrderItem();
  }, [fetchOrderItem]);

  return (
    <>
      <Modal
        open={Boolean(todo)}
        size="large"
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: 'calc(100% - 64px)',
            maxWidth: 'none',
            margin: '32px'
          }
        }}
      >
        <ModalHeader>
          <div>
            作業を行ってください
            {displayTripId && (
              <div style={{ marginTop: '4px' }}>
                <TripIdWithArrangementLink tripId={displayTripId} variant="header" />
              </div>
            )}
          </div>
        </ModalHeader>
        <ModalBody style={{ backgroundColor: '#fff' }}>
          {loading ? (
            <Loading />
          ) : !store ? (
            <div style={{ color: theme.redColor }}>
              {errors.length > 0 ? (
                errors.map((error, index) => <p key={index}>{error}</p>)
              ) : (
                <p>商品情報が取得できませんでした</p>
              )}
            </div>
          ) : (
            <div>
              {errors.length > 0 && (
                <Alert variant="danger" onClose={() => setErrors([])} style={{ marginBottom: '16px' }}>
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </Alert>
              )}
              <OrderItemStepWorkDialogContent ref={contentRef} serviceId={serviceId} store={store} />
            </div>
          )}
        </ModalBody>
        <ModalFooter
          style={{
            borderTop: `1px solid ${theme.tokens.colors.border.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {stateTodo && (
              <>
                <div>
                  <TripIdWithArrangementLink tripId={stateTodo.tripId} variant="footer" />
                </div>
                {stateTodo.traceId && (
                  <div>
                    <span style={{ color: theme.grayTextColor, fontSize: '0.875rem', marginRight: '4px' }}>
                      trace_id🐥:
                    </span>
                    <span style={{ fontSize: '0.875rem' }}>{stateTodo.traceId}</span>
                  </div>
                )}
                <div>
                  <span style={{ color: theme.grayTextColor, fontSize: '0.875rem', marginRight: '4px' }}>
                    請求対象日:
                  </span>
                  <span style={{ fontSize: '0.875rem' }}>
                    {store?.orderItem?.billedAt?.format('YYYY-MM-DD') ?? '-'}
                  </span>
                </div>
                {stateTodo.step && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      backgroundColor:
                        stateTodo.jobType === 0 || stateTodo.jobType === 'reserve'
                          ? '#D9F1FF'
                          : stateTodo.jobType === 1 || stateTodo.jobType === 'change'
                          ? '#DDF2E7'
                          : stateTodo.jobType === 2 || stateTodo.jobType === 'cancel'
                          ? '#E3E3F5'
                          : '#F5F5F5',
                      color:
                        stateTodo.jobType === 0 || stateTodo.jobType === 'reserve'
                          ? '#0080CB'
                          : stateTodo.jobType === 1 || stateTodo.jobType === 'change'
                          ? '#4A9464'
                          : stateTodo.jobType === 2 || stateTodo.jobType === 'cancel'
                          ? '#4A4A94'
                          : '#616161'
                    }}
                  >
                    {OrderItemJobType[stateTodo.jobType] ?? '-'}-
                    {OrderItemStatus[stateTodo.step.targetStatus] || stateTodo.step.finishedStatus}
                  </span>
                )}
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {stateTodo && (
              <>
                <Button
                  onClick={handleRestartClick}
                  style={{
                    fontSize: '0.875rem',
                    marginRight: '8px',
                    padding: 0,
                    minWidth: 'auto',
                    background: 'none',
                    border: 'none',
                    color: theme.linkColor,
                    textDecoration: 'underline',
                    textTransform: 'none'
                  }}
                >
                  シーケンス再発火
                </Button>
                <OrderItemStepDurationCounter todo={stateTodo} />
              </>
            )}
            <Button color="sub" disabled={loading} loading={snoozing} onClick={handleSnoozeClick}>
              スヌーズ
            </Button>
            <Button color="sub" disabled={loading || updating} loading={saving} onClick={handleSave}>
              保存
            </Button>
            <Button color="primary" disabled={loading || saving} loading={updating} onClick={handleSubmit}>
              完了
            </Button>
          </div>
        </ModalFooter>
      </Modal>
      <RestartSequencePopover
        anchorEl={restartAnchorEl}
        onClose={handleRestartPopoverClose}
        onConfirm={handleRestartConfirm}
      />
      <ConfirmModal
        open={restartConfirmOpen}
        onCancel={handleRestartConfirmClose}
        onSubmit={handleRestartExecute}
        submitButtonLabel={restarting ? '処理中...' : 'OK'}
        description={
          restartParams
            ? `現在のシーケンスを破棄して、${JOB_TYPE_LABELS[restartParams.jobType]}のシーケンスを${
                OrderItemStatus[restartParams.targetStatus as keyof typeof OrderItemStatus]
              }ステップから再発火します。よろしいですか？`
            : ''
        }
      />
      <Modal open={criticalErrors.length > 0} onClose={handleSecondaryClose}>
        <ModalHeader>エラーが発生しました</ModalHeader>
        <ModalBody>
          {criticalErrors.map((error, index) =>
            error.type === 'error' ? (
              <p key={index} style={{ color: theme.redColor }}>
                {error.message}
              </p>
            ) : error.type === 'message' ? (
              <p key={index}>{error.message}</p>
            ) : error.type === 'trip_id' ? (
              <p key={index}>
                旅程番号：
                <Link href={`/arrangement/virtual_counter?trip_id=${error.tripId}`} target="_blank">
                  {error.tripId}
                </Link>
              </p>
            ) : null
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSecondaryClose}>
            閉じる
          </Button>
        </ModalFooter>
      </Modal>
      <Modal open={newUserMessageReceived} onClose={handleNewMessageModalClose}>
        <ModalHeader>新しいメッセージ</ModalHeader>
        <ModalBody>
          <p>ユーザーからの新しいメッセージがあります。内容をご確認ください。</p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleNewMessageModalClose}>
            確認
          </Button>
        </ModalFooter>
      </Modal>
      <Modal open={cancelRequestReceived !== null} onClose={() => setCancelRequestReceived(null)}>
        <ModalHeader>キャンセル依頼が入りました</ModalHeader>
        <ModalBody>
          <p>
            {cancelRequestReceived?.isOrderItemCancel
              ? 'この商品にキャンセル依頼が入りました。'
              : 'この旅程に一括キャンセル依頼が入りました。'}
          </p>
          <p>内容を確認してください。</p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setCancelRequestReceived(null)}>
            確認
          </Button>
        </ModalFooter>
      </Modal>
      <Modal open={showSnoozeForm} onClose={handleSnoozeCancel}>
        <ModalHeader>スヌーズ</ModalHeader>
        <ModalBody>
          <div>
            <label style={{ display: 'block', marginBottom: '4px' }}>再通知日時</label>
            <Datepicker
              type="datetime-local"
              value={snoozeTo}
              onChange={d => setSnoozeTo(d ?? undefined)}
              min={new Date()}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="sub" onClick={handleSnoozeCancel}>
            キャンセル
          </Button>
          <Button color="primary" loading={snoozing} onClick={handleSnoozeSave}>
            保存
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default OrderItemStepWorkDialog;
